// Default slot mapping for the single-menu layout. Step 6 lands the
// Designer override JSON — until then this is the canonical structure of
// the layout's content regions. Edits here change every single-menu
// deployment that doesn't override.
//
// Slot ids defined by single-menu:
//   - "left-panel"      brand + finishes + catalog (the glass column)
//   - "tool-rail"       right-edge vertical button stack
//   - "status-card"     bottom-right floating card
//   - "modal:cart"      cart modal body (kept generic in step 4; cart-items
//                       block lands later — for now the modal uses the
//                       existing CartModal renderer via renderOverrides)
//   - "modal:help"      help modal body
//
// Block keys are stable within a slot so future Designer overrides can
// match-and-edit individual entries instead of replacing the whole list.

import type { BlockInstance, SlotMapping } from "../blocks/types";

export const SINGLE_MENU_DEFAULT_MAPPING: SlotMapping = {
  "tool-rail": [
    { block: "action", key: "camera",    props: { actionId: "camera-toggle" } },
    { block: "action", key: "reset",     props: { actionId: "reset" } },
    { block: "action", key: "undo",      props: { actionId: "undo" } },
    { block: "action", key: "redo",      props: { actionId: "redo" } },
    { block: "action", key: "delete",    props: { actionId: "delete" } },
    { block: "action", key: "rotate-cw", props: { actionId: "rotate-clockwise" } },
    { block: "action", key: "rotate-cc", props: { actionId: "rotate-counter-clockwise" } },
    { block: "action", key: "help",      props: { actionId: "help" } },
    { block: "action", key: "scene-looks", props: { actionId: "open-scene-looks" } },
    { block: "action", key: "download",    props: { actionId: "download-model" } },
    // No renderProfileId in the default — the action renders disabled until
    // the operator wires up a profile per Designer. Keeps the icon visible
    // in the rail so operators discover the feature.
    { block: "action", key: "render",      props: { actionId: "render-scene" } },
  ],

  // Series picker — top bar (above the tile gallery). Brand mark sits in
  // the chrome (logo image), so this slot is just the centre title.
  // Designers swap "Choose a series" for their own DVar to translate.
  "series-picker-topbar": [
    {
      block: "text", key: "title",
      props: { variant: "subtitle", text: "chose series" },
    },
  ],

  // Series picker — intro strip above the tiles.
  // Eyebrow + display headline + body paragraph. Designers replace with
  // their own copy / DVars to translate or rephrase.
  // Modal slots. The real cart/help/email modals are still rendered by the
  // host through CartModal / placeholder JSX, but exposing them as slots
  // lets the focus editor preview their authored copy. Once the host
  // migrates to slot-driven modal bodies (commit 3f in step 4), these
  // defaults become the rendered content.
  "modal:help": [
    { block: "text", key: "heading", props: { variant: "heading", text: "Help" } },
    { block: "text", key: "body",    props: { variant: "body",
      text: "Drag products from the catalog into the scene. Click a placed product to change its materials. Use the side rail for camera and editing actions." } },
  ],
  // Presented copy for the built-in Download 3D model modal. Text blocks keyed
  // by the field the modal reads; edit or translate any of them, or clear one to
  // keep the built-in default. The format buttons + download flow are built-in.
  "modal:download-model": [
    { block: "text", key: "heading",  props: { variant: "heading", text: "Download 3D model" } },
    { block: "text", key: "intro",    props: { variant: "body", text: "Pick a format. The download starts immediately when the file is ready." } },
    { block: "text", key: "desc-obj", props: { variant: "body", text: "Wavefront OBJ — opens in most 3D tools." } },
    { block: "text", key: "desc-glb", props: { variant: "body", text: "Binary glTF — standard for web and AR." } },
    { block: "text", key: "desc-stl", props: { variant: "body", text: "STereoLithography — used for 3D printing." } },
  ],
  // Presented copy for the built-in Render scene modal.
  "modal:render-scene": [
    { block: "text", key: "heading",      props: { variant: "heading", text: "Render this scene" } },
    { block: "text", key: "intro",        props: { variant: "body", text: "Send the current scene to be rendered. The render runs in the background and may take a few minutes. We'll open a status page in a new tab — the image appears there as soon as it's ready." } },
    { block: "text", key: "render-label", props: { variant: "body", text: "Render" } },
    { block: "text", key: "cancel-label", props: { variant: "body", text: "Cancel" } },
    { block: "text", key: "done-label",   props: { variant: "body", text: "Done" } },
  ],
  // Cart footer: Download PDF + Email PDF pills. pdfLayoutId empty in the
  // default — operator pastes their PDF Layout id per Designer; until then the
  // buttons render disabled (visible, not hidden) so they're discoverable.
  // Cart totals — stat blocks (element count + scene measurement), replacing the
  // old hardcoded "Total units / Total measurement" composite. Each stat's label
  // + unit are authorable/translatable.
  "modal:cart-totals": [
    { block: "stat", key: "count", props: {
      kind: "count", label: "Total units", unitSingular: "Element", unitPlural: "Elements" } },
    { block: "stat", key: "measurement", props: {
      kind: "measurement", label: "Total measurement", unitSingular: "cm (w × h)" } },
  ],
  "modal:cart-footer": [
    // Close button — a real action block (close-cart → host.closeTopModal), not
    // hardcoded chrome, so operators can relabel / restyle / remove it. The
    // empty actionIcon renders it as a text-only ghost button (the original
    // look); clearing the icon is the operator's call per Designer.
    { block: "action", key: "close", themeVariant: "cart-button-ghost",
      props: { actionId: "close-cart", actionLabel: "Close", actionIcon: "" } },
    { block: "action", key: "pdf-download", themeVariant: "cart-button",
      props: { actionId: "render-pdf-download", actionLabel: "Download PDF", pdfLayoutId: "" } },
    { block: "action", key: "pdf-email", themeVariant: "cart-button-primary",
      props: { actionId: "render-pdf-email", actionLabel: "Email PDF", pdfLayoutId: "" } },
  ],
  // Default PDF email form. The operator overrides pdfLayoutId on the
  // form-submit block per Designer (defaults can't carry tenant-specific
  // PDF Layout ids). Renders TO + CC + MESSAGE inputs and a SEND EMAIL
  // submit; matches the reference layout shipped in v1.
  // Email modal header — a dedicated slot (part of the Email section). Its text
  // blocks author the modal's title + subtitle. The modal renders the first
  // heading block as the title and the first body block as the subtitle.
  "modal:pdf-email-header": [
    { block: "text", key: "title", props: { variant: "heading", text: "Send Project Inventory" } },
    { block: "text", key: "subtitle", props: { variant: "body", text: "Finalizing your specification" } },
  ],
  "modal:pdf-email": [
    { block: "input-email", key: "field-to", props: {
      name: "to", label: "To", placeholder: "client@example.com", required: true } },
    { block: "input-email", key: "field-cc", props: {
      name: "cc", label: "Cc (optional)", placeholder: "colleagues, partners…" } },
    { block: "input-textarea", key: "field-message", props: {
      name: "message", label: "Message", placeholder: "Add a note about this inventory export…", rows: 5 } },
    { block: "form-submit", key: "send", props: {
      actionId: "render-pdf-email-send",
      label: "Send email",
      requiredFields: "to",
      // pdfLayoutId is empty in the default — operator pastes the PDF
      // Layout's Guid per Designer.
      pdfLayoutId: "",
    } },
  ],
  // Composable cart body. The interior is now operator-authored: an eyebrow +
  // title header, then a product-table whose columns are block lists (the same
  // composable model the PDF pdf-product-table uses), reproducing the DK3 look
  // — illustration | name + two description lines | article number | quantity.
  // Sorting / grouping are configurable on the product-table block; the default
  // keeps the familiar A→Z (no sort/group). The product-parameter blocks read
  // "description1"/"description2" — placeholder parameter keys the operator
  // overrides per Designer to point at real catalog parameter definitions.
  "modal:cart": [
    { block: "text", key: "eyebrow", className: "sd-cart__eyebrow",
      props: { variant: "eyebrow", text: "Project inventory" } },
    { block: "text", key: "title", className: "sd-cart__title",
      props: { variant: "heading-display", source: "from-active-series-label", text: "Element list" } },
    {
      block: "product-table", key: "table",
      props: {
        columns: [
          { header: "Illustration", widthPx: 120, align: "left", blocks: [
            { block: "product-image", key: "img" },
          ] },
          { header: "Product description", align: "left", blocks: [
            { block: "product-name", key: "name" },
            { block: "product-parameter", key: "desc1", props: { parameterKey: "param:description1" } },
            { block: "product-parameter", key: "desc2", props: { parameterKey: "param:description2" } },
          ] },
          { header: "Article number", widthPx: 160, align: "left", blocks: [
            // Dedicated column → prominent headline treatment (accent variant).
            { block: "product-sku", key: "sku", themeVariant: "accent" },
          ] },
          { header: "Quantity", widthPx: 80, align: "right", blocks: [
            { block: "product-quantity", key: "qty" },
          ] },
        ],
      },
    },
  ],
  // Mobile cart body. Same header, but a narrower two-data-column table: the
  // Article Number column is dropped and the SKU renders as a line beneath the
  // product's name + descriptions, so the cramped mobile width goes to the
  // description text instead of a dedicated column. The host renders this slot
  // instead of modal:cart at mobile widths; leave it empty in an override to
  // fall back to the desktop modal:cart body.
  "modal:cart-mobile": [
    { block: "text", key: "eyebrow", className: "sd-cart__eyebrow",
      props: { variant: "eyebrow", text: "Project inventory" } },
    { block: "text", key: "title", className: "sd-cart__title",
      props: { variant: "heading-display", source: "from-active-series-label", text: "Element list" } },
    {
      block: "product-table", key: "table",
      props: {
        columns: [
          { header: "Illustration", widthPx: 88, align: "left", blocks: [
            { block: "product-image", key: "img" },
          ] },
          { header: "Product", align: "left", blocks: [
            { block: "product-name", key: "name" },
            { block: "product-parameter", key: "desc1", props: { parameterKey: "param:description1" } },
            { block: "product-parameter", key: "desc2", props: { parameterKey: "param:description2" } },
            // Article number as a line under the descriptions — no column of its own.
            { block: "product-sku", key: "sku" },
          ] },
          { header: "Qty", widthPx: 56, align: "right", blocks: [
            { block: "product-quantity", key: "qty" },
          ] },
        ],
      },
    },
  ],
  // Default scene-looks picker: a heading + two example presets. The action
  // block's actionLabel/actionIcon override the underlying ActionDef so each
  // preset shows its own caption; dmatBindings is empty in the default —
  // operators fill in actual DMat ids per Designer (defaults can't carry
  // tenant-specific asset ids).
  "modal:scene-looks": [
    { block: "text", key: "heading", props: { variant: "heading", text: "Choose a scene" } },
    { block: "text", key: "subtitle", props: { variant: "subtitle",
      text: "Pick a wall + floor combo. Customise the materials per preset in the dashboard editor." } },
    { block: "action", key: "look-warm",
      props: { actionId: "apply-scene-materials", actionLabel: "Warm", actionIcon: "wb_sunny", dmatBindings: [] } },
    { block: "action", key: "look-cool",
      props: { actionId: "apply-scene-materials", actionLabel: "Cool", actionIcon: "ac_unit", dmatBindings: [] } },
  ],

  // Startup slot — non-rendering. Action blocks placed here fire once Unity
  // reports ready (see SceneDesignerTool's startup effect). Empty in the
  // default; operators add an apply-scene-materials entry per Designer to
  // boot with their chosen scene look.
  startup: [],

  "series-picker-intro": [
    {
      block: "text", key: "eyebrow",
      props: { variant: "eyebrow", text: "Configurator" },
    },
    {
      block: "text", key: "headline",
      props: { variant: "heading-display", text: "Begins with a series" },
    },
    {
      block: "text", key: "body",
      props: {
        variant: "body",
        text: "Pick a starting point. Every series can be configured from this catalog — materials and components reflect the choice.",
      },
    },
  ],

  // The series tiles. This slot's blocks render ONCE PER SERIES — each block is
  // bound to the current series via SeriesContext (same pattern as the product
  // table's per-row blocks). The picker owns the tile frame + click-to-pick +
  // hover; these blocks fill the tile's content. Default tile: hero image,
  // series name, then a "Configurate" call-to-action.
  "series-picker-tiles": [
    { block: "series-image", key: "image" },
    { block: "series-name", key: "name", props: { variant: "heading" } },
    { block: "text", key: "cta", className: "sd-eg-series__tile-cta",
      props: { variant: "body", text: "Configurate" } },
  ],

  "left-panel": [
    // Panel header — eyebrow + active series name in display serif. The
    // brand mark itself renders from the layout chrome (logo at the panel's
    // top-left); these blocks sit at the top of the scrollable content.
    // A thin divider sits above "Configurator" (below the logo) per the
    // DK3 redesign.
    { block: "line", key: "logo-line" },
    {
      block: "text", key: "panel-eyebrow",
      props: { variant: "eyebrow", text: "Configurator" },
    },
    {
      block: "text", key: "panel-series-name",
      props: { variant: "heading-display", source: "from-active-series-label" },
    },
    { block: "line", key: "header-line" },

    {
      block: "text", key: "finishes-heading",
      props: { variant: "heading", text: "Finishes" },
    },
    {
      block: "text", key: "finishes-sub-empty",
      props: { variant: "subtitle", text: "Configure default products" },
      showWhen: "selection-empty",
    },
    {
      block: "text", key: "finishes-sub-current",
      props: { variant: "subtitle", text: "Configure current selection" },
      showWhen: "selection-present",
    },
    { block: "material-collections", key: "finishes" },
    { block: "line", key: "finishes-line" },
    {
      block: "text", key: "components-heading",
      props: { variant: "heading", text: "Components" },
    },
    {
      block: "text", key: "components-eyebrow",
      props: { variant: "eyebrow", text: "Drag and drop to compose" },
    },
    {
      block: "catalog", key: "catalog",
      props: { variant: "editorial-row" },
      // specParameterKeys is intentionally empty in the layout default —
      // every project wires its own keys depending on what parameters its
      // products carry. A Designer override fills this in:
      //   props: { variant: "editorial-row", specParameterKeys: ["height", "material"] }
    },
  ],

  // ── Notifications (global toasts) ───────────────────────────────────────
  // Rendered at the top of the screen on every layout / breakpoint. Each
  // `notification` block declares a `trigger` (a NotificationTrigger the app
  // fires via host.notify), a `text`/`dvarId` message, and a `timeout` in ms
  // (0 = stays until dismissed). Operators add / translate these per Designer.
  "notifications": [
    {
      block: "notification", key: "touch-drag-hint",
      props: {
        trigger: "product-tap-no-drag",
        text: "Drag a product into the scene using the handle on the right.",
        timeout: 4000,
      },
    },
    {
      block: "notification", key: "mouse-drag-hint",
      props: {
        trigger: "product-click-no-drag",
        text: "Drag a product into the scene to place it.",
        timeout: 4000,
      },
    },
    {
      block: "notification", key: "drag-cancelled-hint",
      props: {
        trigger: "drag-cancelled",
        text: "Drop the product onto the scene to place it.",
        timeout: 4000,
      },
    },
  ],

  // ── Mobile slots ──────────────────────────────────────────────────────
  // At narrow widths the layout renders <MobileSheet> instead of the glass
  // side panel + floating status card. The sheet's two tabs and its fixed
  // status bar each read one of these slots. Content reuses the same blocks
  // as the desktop left-panel — material-collections for finishes, the
  // editorial-row catalog for components — so a Designer override of these
  // slots customises mobile independently of desktop.
  //
  // These static entries are the *last-resort* fallback. The layout prefers,
  // in order: (1) an explicit Designer override of the mobile slot, (2) content
  // derived from the Designer's left-panel (so catalog props like
  // specParameterKeys carry over to mobile automatically — see
  // deriveMobileTabsFromLeftPanel), (3) these defaults. They only render when
  // the left-panel has no catalog / material-collections block to derive from.
  // Mobile bottom-sheet tab captions — one keyed text block per tab. The layout
  // reads these by key (materials / products / cart) to caption the tab strip;
  // translatable like any other text block. Replaces the old per-slot parked
  // "tab-label" blocks.
  "mobile:menu-tabs": [
    { block: "text", key: "materials", props: { variant: "body", text: "Materials" } },
    { block: "text", key: "products", props: { variant: "body", text: "Products" } },
    { block: "text", key: "cart", props: { variant: "body", text: "PDF" } },
  ],
  "mobile:materials": [
    {
      block: "text", key: "m-finishes-sub-empty",
      props: { variant: "subtitle", text: "Configure default products" },
      showWhen: "selection-empty",
    },
    {
      block: "text", key: "m-finishes-sub-current",
      props: { variant: "subtitle", text: "Configure current selection" },
      showWhen: "selection-present",
    },
    { block: "material-collections", key: "m-finishes" },
  ],
  "mobile:products": [
    {
      block: "text", key: "m-components-eyebrow",
      props: { variant: "eyebrow", text: "Drag the handle to place in the scene" },
    },
    {
      block: "catalog", key: "m-catalog",
      props: { variant: "editorial-row" },
    },
  ],
  // On-selection popup — horizontal card that slides up from the bottom when
  // a placed product is selected. Title (live selected product name) sits at
  // the top with a hairline beneath, then the per-surface picker as a row of
  // columns. Operators can swap the text variant, replace the surfaces block
  // with their own composition, or hide the title entirely.
  "selection-popup": [
    // Delete + variant toggle float to the top-right corner of the popup
    // via layout-scoped CSS (.sd-eg-selection-card > .sd-block-action).
    // The variant toggle's default key is "open" — operators retarget it
    // per-Designer by editing the block's variantKey prop. Products
    // without an "open" variant render the button disabled.
    { block: "action", key: "delete", props: { actionId: "delete" } },
    { block: "action", key: "variant-toggle",
      props: { actionId: "toggle-variant", variantKey: "open" } },
    {
      block: "text", key: "selected-name",
      props: { variant: "heading", source: "from-selected-product-name" },
    },
    { block: "line", key: "name-line" },
    { block: "selection-surfaces", key: "surfaces" },
  ],

  "status-card": [
    {
      block: "group", key: "card",
      props: { variant: "accent-bar" },
      children: [
        {
          block: "stat", key: "count",
          props: {
            kind: "count", label: "Configured items",
            // Unit noun appended to the count — authorable + translatable.
            unitSingular: "Element", unitPlural: "Elements",
          },
        },
        // Price block renders only once a price source exists (step 8 wires
        // sceneFeatures.totalPrice; until then this is a no-op placeholder).
        {
          block: "stat", key: "price",
          props: { kind: "price", label: "Total" },
        },
        {
          block: "group", key: "footer",
          props: { variant: "row" },
          children: [
            { block: "action", key: "back", props: { actionId: "back-to-series" } },
            {
              block: "action", key: "cart",
              props: { actionId: "open-cart" },
              themeVariant: "primary",
            },
          ],
        },
      ],
    },
  ],
};

// ── Mobile-tab derivation from the left panel ───────────────────────────────
// The mobile Materials / Products tabs should show the same finishes picker and
// product catalog the Designer configured for the desktop left panel — including
// any catalog props like specParameterKeys. Rather than make the operator
// re-author those settings under the mobile slots, we lift the relevant blocks
// straight out of the resolved left-panel mapping.
//
// "Materials" tab ← the material-collections block plus its immediately
//   surrounding subtitle text blocks (the "Configure default/current" lines).
// "Products" tab  ← the catalog block, with its props (variant, specParameterKeys)
//   intact, fronted by a short drag hint.
//
// Returns null for a tab when the left panel has no block to derive from — the
// caller then falls back to the static mobile defaults above. Block keys are
// re-prefixed ("m-") so they don't collide with the desktop instances if both
// ever render in the same tree (focus-mode previews).

const DRAG_HINT_BLOCK: BlockInstance = {
  block: "text", key: "m-components-eyebrow",
  props: { variant: "eyebrow", text: "Drag the handle to place in the scene" },
};

/** Pull the finishes (material-collections) section out of a resolved
 *  left-panel. Includes the subtitle text blocks that sit directly around the
 *  picker so the "Configure default/current selection" labelling carries over.
 *  Null when the panel has no material-collections block. */
export function deriveMobileMaterialsFromLeftPanel(
  leftPanel: BlockInstance[] | undefined
): BlockInstance[] | null {
  if (!leftPanel || leftPanel.length === 0) return null;
  const out: BlockInstance[] = [];
  let found = false;
  for (const b of leftPanel) {
    if (b.block === "material-collections") {
      found = true;
      out.push({ ...b, key: prefix(b.key) });
      continue;
    }
    // Carry the finishes subtitle helpers (they use showWhen selection-empty /
    // selection-present). Identify them by their stable default keys so we
    // don't sweep in unrelated text like the panel header / series name.
    if (b.block === "text" && (b.key === "finishes-sub-empty" || b.key === "finishes-sub-current")) {
      out.push({ ...b, key: prefix(b.key) });
    }
  }
  return found ? out : null;
}

/** Pull the catalog section out of a resolved left-panel, preserving its props
 *  (variant, specParameterKeys, …). Null when the panel has no catalog block. */
export function deriveMobileProductsFromLeftPanel(
  leftPanel: BlockInstance[] | undefined
): BlockInstance[] | null {
  if (!leftPanel || leftPanel.length === 0) return null;
  const catalog = leftPanel.find(b => b.block === "catalog");
  if (!catalog) return null;
  return [DRAG_HINT_BLOCK, { ...catalog, key: prefix(catalog.key) }];
}

function prefix(key: string): string {
  return key.startsWith("m-") ? key : "m-" + key;
}
